import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, GraduationCap, Plus, Clock, Users } from 'lucide-react';
import { atlasApi, AtlasUser, Treinamento } from '../shared/atlasApi';
import BadgeStatusT from './BadgeStatusT';
import Detalhe from './Detalhe';
import ModalNovo from './ModalNovo';
import MeusTreinamentos from './MeusTreinamentos';
import Calendario from './Calendario';

interface Props {
  user: AtlasUser;
  podeEditar: boolean;
}

type View = 'lista' | 'calendario' | 'detalhe' | 'meus';

const LABEL_VIEW: Record<Exclude<View, 'detalhe'>, string> = {
  lista: 'Disponíveis',
  calendario: 'Calendário',
  meus: 'Meus treinamentos',
};

function fdata(s?: string | null) {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('pt-BR'); } catch { return s; }
}

const TreinamentosApp: React.FC<Props> = ({ user, podeEditar }) => {
  const [view, setView] = useState<View>('lista');
  const [itens, setItens] = useState<Treinamento[]>([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [selecionado, setSelecionado] = useState<string | null>(null);
  const [modal, setModal] = useState<{ aberto: boolean; editar: Treinamento | null }>({ aberto: false, editar: null });

  const [fCategoria, setFCategoria] = useState('');
  const [fStatus, setFStatus] = useState('');

  const carregar = useCallback(() => {
    setLoading(true);
    setErro(null);
    atlasApi.listarTreinamentos(user, { categoria: fCategoria || undefined, status: fStatus || undefined })
      .then(r => setItens(r.itens || []))
      .catch(e => setErro(e.message || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [user, fCategoria, fStatus]);

  useEffect(() => { if (view === 'lista') carregar(); }, [view, carregar]);

  if (view === 'detalhe' && selecionado) {
    return (
      <div className="p-6">
        <Detalhe
          user={user}
          treinamentoId={selecionado}
          podeEditar={podeEditar}
          onVoltar={() => { setView('lista'); setSelecionado(null); }}
          onEditar={(t) => setModal({ aberto: true, editar: t })}
        />
        {modal.aberto && (
          <ModalNovo user={user} treinamento={modal.editar} onClose={() => setModal({ aberto: false, editar: null })} onSalvo={() => { setModal({ aberto: false, editar: null }); }} />
        )}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center"><GraduationCap className="w-5 h-5" /></div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-gray-900">Treinamentos</h2>
          <p className="text-sm text-gray-500">Capacitação e desenvolvimento do time.</p>
        </div>
        {podeEditar && (
          <button onClick={() => setModal({ aberto: true, editar: null })} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium flex items-center gap-2">
            <Plus className="w-4 h-4" /> Novo treinamento
          </button>
        )}
      </div>

      <div className="flex gap-1 border-b border-gray-200">
        {(['lista', 'calendario', 'meus'] as const).map(v => (
          <button key={v} onClick={() => setView(v)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${view === v ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
            {LABEL_VIEW[v]}
          </button>
        ))}
      </div>

      {view === 'meus' ? (
        <MeusTreinamentos user={user} />
      ) : view === 'calendario' ? (
        <Calendario user={user} onAbrir={id => { setSelecionado(id); setView('detalhe'); }} />
      ) : (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <input value={fCategoria} onChange={e => setFCategoria(e.target.value)} placeholder="Filtrar categoria..." className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-200" />
            <select value={fStatus} onChange={e => setFStatus(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white">
              <option value="">Todos os status</option>
              <option value="publicado">Publicado</option>
              <option value="rascunho">Rascunho</option>
              <option value="encerrado">Encerrado</option>
            </select>
          </div>

          {erro && <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">{erro}</div>}

          {loading ? (
            <div className="p-12 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...</div>
          ) : itens.length === 0 ? (
            <div className="p-12 text-center text-gray-400 bg-white rounded-2xl border border-gray-200">Nenhum treinamento encontrado.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {itens.map(t => (
                <button key={t._id} onClick={() => { setSelecionado(t._id); setView('detalhe'); }} className="text-left bg-white rounded-2xl border border-gray-200 p-5 hover:border-purple-300 hover:shadow-md transition">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-gray-900 flex-1">{t.titulo}</h3>
                    <BadgeStatusT status={t.status} />
                  </div>
                  {t.categoria && <p className="text-xs text-gray-400 mt-0.5">{t.categoria} · {t.modalidade}</p>}
                  {t.descricao && <p className="text-sm text-gray-600 mt-2 line-clamp-2">{t.descricao}</p>}
                  <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {t.carga_horaria || '—'}h</span>
                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {t.vagas_preenchidas || 0}{t.vagas ? `/${t.vagas}` : ''}</span>
                    <span>{fdata(t.data_inicio)}</span>
                    {t.obrigatorio && <span className="text-amber-600 font-medium">Obrigatório</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {modal.aberto && view !== 'detalhe' && (
        <ModalNovo user={user} treinamento={modal.editar} onClose={() => setModal({ aberto: false, editar: null })} onSalvo={() => { setModal({ aberto: false, editar: null }); carregar(); }} />
      )}
    </div>
  );
};

export default TreinamentosApp;
